import React, { useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const TIMEZONES = typeof Intl.supportedValuesOf === 'function' ? Intl.supportedValuesOf('timeZone') : ['UTC'];
const DATE_FORMATS = ['DDMMYY', 'DDMMYYYY', 'MMDDYY', 'MMDDYYYY', 'YYMMDD', 'YYYYMMDD'];
const TIME_FORMATS = ['HHMM', 'HHMMSS'];
const SAME = 'same';
const SEGMENT_FIELDS = [
  ['branch', 'Branch / store'], ['date', 'Transaction date'], ['time', 'Transaction time'],
  ['reference', 'Reference / unique suffix'], ['ignore', 'Ignore'],
];
const QR_PARAMS = [
  ['code_param', 'Full code parameter', 'parsed with the manual code segments'],
  ['branch_param', 'Branch / store parameter', ''],
  ['date_param', 'Transaction date parameter', ''],
  ['time_param', 'Transaction time parameter', 'same name as date if combined'],
  ['reference_param', 'Transaction / reference ID parameter', ''],
];

const PARAM_RE = /^[A-Za-z_][A-Za-z0-9_.-]{0,39}$/;
export const qrParamError = (label, val) => {
  if (!val || PARAM_RE.test(val)) return '';
  const name = val.split('=')[0].replace(/^[?&]+/, '').trim();
  if (val.includes('=') && PARAM_RE.test(name)) return `${label}: enter only the parameter name '${name}', not '${val}'.`;
  if (/^[0-9]/.test(val) && val.length <= 64) {
    return `${label}: '${val}' looks like a value from a QR link, not a parameter name. Enter only the name before '=' (e.g. for ?ref=${val} enter ref).`;
  }
  return `${label}: '${val}' isn't a valid parameter name. Use up to 40 letters, digits, _ . or -, starting with a letter (e.g. store_code).`;
};
export const firstQrParamError = (qr) => QR_PARAMS.map(([key, label]) => qrParamError(label, qr?.[key])).find(Boolean) || '';

export const Label = ({ children }) => <label className="text-xs font-semibold text-[#334155] block mb-1.5">{children}</label>;
export const Section = ({ title, children, testid }) => (
  <fieldset className="border border-[#E2E8F0] rounded-lg p-3 space-y-3" data-testid={testid}>
    <legend className="text-xs font-bold text-[#0F172A] px-1">{title}</legend>
    {children}
  </fieldset>
);
export const Toggle = ({ label, checked, onChange, testid }) => (
  <div className="flex items-center justify-between gap-3">
    <span className="text-xs text-[#334155]">{label}</span>
    <Switch checked={!!checked} onCheckedChange={onChange} data-testid={testid} />
  </div>
);
const Check = ({ label, checked, onChange, testid }) => (
  <label className="flex items-center gap-2 text-xs text-[#334155] cursor-pointer">
    <Checkbox checked={!!checked} onCheckedChange={(v) => onChange(!!v)} data-testid={testid} /> {label}
  </label>
);
export const SimpleSelect = ({ value, onChange, options, testid }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger data-testid={testid}><SelectValue /></SelectTrigger>
    <SelectContent>{options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
  </Select>
);

const ValidityInput = ({ hours, onChange }) => {
  const [unit, setUnit] = useState(hours && hours % 24 === 0 ? 'days' : 'hours');
  const factor = unit === 'days' ? 24 : 1;
  const shown = hours === '' || hours == null ? '' : hours / factor;
  return (
    <div className="flex gap-2">
      <Input type="number" min="1" value={shown} placeholder="No expiry" className="flex-1"
        onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value) * factor)} data-testid="access-validity-input" />
      <div className="w-28"><SimpleSelect value={unit} onChange={setUnit} options={[['hours', 'Hours'], ['days', 'Days']]} testid="access-validity-unit" /></div>
    </div>
  );
};

// Phase 4B — the only settings shown in the normal workflow.
export const SimpleSettings = ({ form, set }) => (
  <Section title="Access rules (apply to every branch)" testid="access-simple-settings">
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
      <Toggle label="QR Direct Access" checked={form.methods.qr} onChange={(v) => set(['methods', 'qr'], v)} testid="access-method-qr" />
      <Toggle label="Manual Numeric Code" checked={form.methods.manual} onChange={(v) => set(['methods', 'manual'], v)} testid="access-method-manual" />
      <Toggle label="Single use" checked={form.single_use} onChange={(v) => set(['single_use'], v)} testid="access-single-use-switch" />
      <Toggle label="Branch validation" checked={form.branch_validation} onChange={(v) => set(['branch_validation'], v)} testid="access-branch-validation-switch" />
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <div>
        <Label>Timezone</Label>
        <Input list="access-tz-list" value={form.timezone} onChange={(e) => set(['timezone'], e.target.value)} data-testid="access-timezone-input" />
        <datalist id="access-tz-list">{TIMEZONES.map(z => <option key={z} value={z} />)}</datalist>
      </div>
      <div>
        <Label>Validity period (from the transaction time)</Label>
        <ValidityInput hours={form.validity_hours} onChange={(v) => set(['validity_hours'], v)} />
      </div>
    </div>
  </Section>
);

export const RequirementSettings = ({ form, set }) => (
  <Section title="Requirement & branch matching" testid="access-requirement-settings">
    <div className="w-48">
      <Label>Access requirement</Label>
      <SimpleSelect value={form.required ? 'required' : 'optional'} onChange={(v) => set(['required'], v === 'required')}
        options={[['required', 'Required'], ['optional', 'Optional']]} testid="access-required-select" />
    </div>
    <Toggle label="Also match incoming store codes to satisFIND branch codes" checked={form.match_branch_code}
      onChange={(v) => set(['match_branch_code'], v)} testid="access-match-branch-code-switch" />
  </Section>
);

const SegmentRow = ({ seg, onChange, onRemove, i }) => (
  <div className="flex items-center gap-2" data-testid={`access-segment-row-${i}`}>
    <div className="flex-1 min-w-0"><SimpleSelect value={seg.field} onChange={(v) => onChange({ ...seg, field: v })} options={SEGMENT_FIELDS} testid={`access-segment-field-${i}`} /></div>
    <Input type="number" min="1" value={seg.start} onChange={(e) => onChange({ ...seg, start: e.target.value })} className="w-20" placeholder="From" data-testid={`access-segment-start-${i}`} />
    <Input type="number" min="1" value={seg.length ?? ''} onChange={(e) => onChange({ ...seg, length: e.target.value })} className="w-24" placeholder="Rest" data-testid={`access-segment-length-${i}`} />
    <Button type="button" variant="ghost" size="icon" onClick={onRemove} data-testid={`access-segment-remove-${i}`}><Trash2 className="w-4 h-4 text-[#94A3B8]" /></Button>
  </div>
);

export const ManualCodeSettings = ({ form, set }) => {
  const segs = form.manual.segments || [];
  const update = (next) => set(['manual', 'segments'], next);
  const nextStart = segs.reduce((m, s) => Math.max(m, Number(s.start || 1) + Number(s.length || 0)), 1);
  return (
    <Section title="Manual code parser" testid="access-manual-settings">
      <div className="flex flex-wrap gap-5">
        <Check label="Remove spaces" checked={form.manual.strip_spaces} onChange={(v) => set(['manual', 'strip_spaces'], v)} testid="access-strip-spaces" />
        <Check label="Remove hyphens" checked={form.manual.strip_hyphens} onChange={(v) => set(['manual', 'strip_hyphens'], v)} testid="access-strip-hyphens" />
        <Check label="Digits only" checked={form.manual.numeric_only} onChange={(v) => set(['manual', 'numeric_only'], v)} testid="access-numeric-only" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div><Label>Manual date format</Label><SimpleSelect value={form.date_format} onChange={(v) => set(['date_format'], v)} options={DATE_FORMATS.map(f => [f, f])} testid="access-date-format" /></div>
        <div><Label>Manual time format</Label><SimpleSelect value={form.time_format} onChange={(v) => set(['time_format'], v)} options={TIME_FORMATS.map(f => [f, f])} testid="access-time-format" /></div>
        <div>
          <Label>Exact code length (optional)</Label>
          <Input type="number" min="1" value={form.manual.length} onChange={(e) => set(['manual', 'length'], e.target.value)} placeholder="Any" data-testid="access-code-length-input" />
        </div>
      </div>
      <div className="space-y-2">
        <p className="text-[11px] text-[#64748B]">Segments: character position (from 1) and length. Leave length blank on the last segment to take the remaining characters.</p>
        {segs.map((s, i) => (
          <SegmentRow key={i} i={i} seg={s} onChange={(v) => update(segs.map((x, j) => (j === i ? v : x)))} onRemove={() => update(segs.filter((_, j) => j !== i))} />
        ))}
        <Button type="button" variant="outline" size="sm" onClick={() => update([...segs, { field: 'reference', start: nextStart, length: '' }])} data-testid="access-segment-add-btn">
          <Plus className="w-3.5 h-3.5 mr-1" /> Add segment
        </Button>
      </div>
    </Section>
  );
};

export const QrSettings = ({ form, set }) => (
  <Section title="QR link parameters" testid="access-qr-settings">
    <p className="text-[11px] text-[#64748B]">URL query parameter names your QR links carry (e.g. ?store_code=…&date=…). Leave blank if not used.</p>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      {QR_PARAMS.map(([key, label, hint]) => {
        const err = qrParamError(label, form.qr[key]);
        return (
          <div key={key}>
            <Label>{label}</Label>
            <Input value={form.qr[key]} onChange={(e) => set(['qr', key], e.target.value.trim())} placeholder={hint || 'parameter name'}
              aria-invalid={!!err} className={err ? 'border-[#B91C1C] focus-visible:ring-[#B91C1C]' : ''} data-testid={`access-qr-${key}`} />
            {err && <p className="text-[11px] text-[#B91C1C] mt-1" data-testid={`access-qr-${key}-error`}>{err}</p>}
          </div>
        );
      })}
      <div>
        <Label>QR date format</Label>
        <SimpleSelect value={form.qr_date_format || SAME} onChange={(v) => set(['qr_date_format'], v === SAME ? '' : v)}
          options={[[SAME, 'Same as manual code'], ...DATE_FORMATS.map(f => [f, f])]} testid="access-qr-date-format" />
      </div>
      <div>
        <Label>QR time format</Label>
        <SimpleSelect value={form.qr_time_format || SAME} onChange={(v) => set(['qr_time_format'], v === SAME ? '' : v)}
          options={[[SAME, 'Same as manual code'], ...TIME_FORMATS.map(f => [f, f])]} testid="access-qr-time-format" />
      </div>
    </div>
    <p className="text-[11px] text-[#64748B]">Combined timestamp (e.g. 20250924170753): use the same parameter name for date and time, with YYYYMMDD + HHMMSS.</p>
  </Section>
);
